import * as React from 'react';
import { Dialog, Box, Button, Checkbox, FormControlLabel, DialogActions, DialogContent, DialogTitle, TextField, Switch, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../../config/apiClient';

interface SendersModalProps {
  open: boolean;
  handleClose: () => void;
  selectedTenant: string;
  dateRange: any;
}

export default function SendersModal({ open, handleClose, selectedTenant, dateRange }: SendersModalProps) {

  const [emails, setEmails] = useState<string[]>([]);
  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [saveEmails, setSaveEmails] = React.useState(false);
  const [emailsValue, setEmailsValue] = useState('');

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailsValue(event.target.value);
  };

  const handleCheckboxChange = (email: string) => {
    setSelectedEmails((prevSelected) =>
      prevSelected.includes(email)
        ? prevSelected.filter(e => e !== email)
        : [...prevSelected, email]
    );
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSaveEmails(event.target.checked);
  };

  const validateEmails = (emails: string) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailList = emails.split(',').map(email => email.trim());

    for (let email of emailList) {
      if (!emailPattern.test(email)) {
        return false;
      }
    }

    return true;
  };

  const handleEmail = () => {
    if (selectedEmails.length === 0 && !emailsValue) {
      toast.warn('Please select at least one email or enter a valid email address.', {
        position: 'top-right',
      });
      return;
    }

    if (emailsValue && !validateEmails(emailsValue)) {
      toast.error('Invalid email format. Please check your input.', {
        position: 'top-right',
      });
      return;
    }
  
    const startDate = dateRange.startDate.toISOString().split('T')[0];
    const endDate = dateRange.endDate.toISOString().split('T')[0];
    const dataEmail = {
      emails: selectedEmails.join(','),
      newEmails: emailsValue,
      saveEmails: saveEmails,
      tenantId: selectedTenant,
      startDate: startDate,
      endDate: endDate
    };

    if (selectedTenant && dateRange.startDate && dateRange.endDate) {
      apiClient.post('/email/send', dataEmail)
        .then((response) => {
          toast.success('Emails sent successfully!', {
            position: 'top-right',
          });
        })
        .catch(err => {
          console.error('Error sending email:', err);
          toast.error('Failed to send emails.', {
            position: 'top-right',
          });
        })
        .finally(() => {
          handleClose();
          setEmailsValue('');
        });
    } else {
      toast.warn('Please select a tenant and date range.', {
        position: 'top-right',
      });
    }
  };

  useEffect(() => {
    apiClient.get(`/email/recipients/tenant/${selectedTenant}`)
      .then(response => {
        setEmails(response.data.map((register: { email: string; }) => register.email));
      })
      .catch(error => console.error('Error fetching tenants:', error));
  }, [open, selectedTenant]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Select Emails</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column">
          {emails.map((email, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={selectedEmails.includes(email)}
                  onChange={() => handleCheckboxChange(email)}
                />
              }
              label={email}
            />
          ))}
        </Box>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" style={{ fontSize: '0.85rem', marginRight: '5px' }}>
            You can add more emails separated by commas
          </Typography>
          <Tooltip title="Use commas to separate multiple emails" placement="top" arrow>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <TextField
            autoFocus
            required
            margin="dense"
            id="emails"
            name="emails"
            label="Email Address"
            type="text"
            fullWidth
            variant="standard"
            value={emailsValue}
            onChange={handleEmailChange}
          />
        </Box>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Switch
                checked={saveEmails}
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label="Save new emails?"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleEmail}>Send Email</Button>
      </DialogActions>
    </Dialog>
  );
}
